import {
  Decimal,
  FIXTURE_NOW,
  fixtureId,
  maskDocument,
  maskPhone,
  type DocumentType,
  type ScheduleRow,
} from '@paguz/contracts';

/**
 * Datos semilla del mock (personas y comercios de `03-demo/02-datos-semilla.md`). Todo ficticio.
 * Las cifras de cuotas/intereses son **valores fijos de ejemplo** escritos a mano (no se calculan
 * aquí): en la API real vienen de Credit Core.
 */
export const SEED_NOW = FIXTURE_NOW;
export const DAY_MS = 86_400_000;

export function isoAt(offsetMs: number, base: Date = SEED_NOW): string {
  return new Date(base.getTime() + offsetMs).toISOString();
}

export function businessDateAt(offsetDays: number): string {
  return isoAt(offsetDays * DAY_MS).slice(0, 10);
}

export const cop = (amount: string) => ({ amount, currency: 'COP' as const });

// ── Personas ──────────────────────────────────────────────────────────────
export interface SeedCustomer {
  id: string;
  fullName: string;
  documentType: DocumentType;
  documentNumber: string;
  phone: string;
  businessName: string;
  city: string;
  status: 'ACTIVE' | 'PROSPECT' | 'BLOCKED';
  createdAt: string;
}

export const CUSTOMERS = {
  rosa: {
    id: fixtureId('customer', 11),
    fullName: 'Rosa Martínez',
    documentType: 'CC',
    documentNumber: '52111222',
    phone: '+573001112233',
    businessName: 'Tienda Rosita',
    city: 'Bogotá – Kennedy',
    status: 'ACTIVE',
    createdAt: isoAt(-125 * DAY_MS),
  },
  jairo: {
    id: fixtureId('customer', 12),
    fullName: 'Jairo Pérez',
    documentType: 'CC',
    documentNumber: '79333444',
    phone: '+573102223344',
    businessName: 'Taller El Pistón',
    city: 'Soacha',
    status: 'PROSPECT',
    createdAt: isoAt(-2 * DAY_MS),
  },
  luzDary: {
    id: fixtureId('customer', 13),
    fullName: 'Luz Dary Gómez',
    documentType: 'CC',
    documentNumber: '39555666',
    phone: '+573113334455',
    businessName: 'Panadería La Espiga',
    city: 'Medellín – Robledo',
    status: 'PROSPECT',
    createdAt: isoAt(-9 * DAY_MS),
  },
  hernan: {
    id: fixtureId('customer', 14),
    fullName: 'Hernán Castillo',
    documentType: 'CC',
    documentNumber: '80777888',
    phone: '+573124445566',
    businessName: 'Miscelánea El Trébol',
    city: 'Cali',
    status: 'ACTIVE',
    createdAt: isoAt(-160 * DAY_MS),
  },
} as const satisfies Record<string, SeedCustomer>;

export type CustomerKey = keyof typeof CUSTOMERS;

export function customerRef(key: CustomerKey) {
  const c = CUSTOMERS[key];
  return {
    id: c.id,
    fullName: c.fullName,
    maskedDocument: maskDocument(c.documentNumber),
    maskedPhone: maskPhone(c.phone),
  };
}

// ── Comercios (directorio Bre-B del sandbox) ──────────────────────────────
export interface SeedMerchant {
  key: string;
  name: string;
  aliasType: 'PHONE' | 'ALPHANUMERIC';
  aliasValue: string;
  participantCode: string;
  participantName: string;
  mcc: string;
  city: string;
}

export const MERCHANTS = {
  donPedro: {
    key: 'donPedro',
    name: 'Ferretería Don Pedro',
    aliasType: 'PHONE',
    aliasValue: '+573105550101',
    participantCode: '901',
    participantName: 'Banco ficticio A',
    mcc: '5251',
    city: 'Bogotá',
  },
  mayorista: {
    key: 'mayorista',
    name: 'Distribuidora El Mayorista del Sur',
    aliasType: 'ALPHANUMERIC',
    aliasValue: '@mayoristadelsur',
    participantCode: '902',
    participantName: 'Banco ficticio B',
    mcc: '5411',
    city: 'Bogotá',
  },
  lacteos: {
    key: 'lacteos',
    name: 'Lácteos San Isidro',
    aliasType: 'ALPHANUMERIC',
    aliasValue: '@lacteossanisidro',
    participantCode: '903',
    participantName: 'Cooperativa ficticia',
    mcc: '5451',
    city: 'Bogotá',
  },
  llanero: {
    key: 'llanero',
    name: 'Carnes Frías El Llanero',
    aliasType: 'ALPHANUMERIC',
    aliasValue: '@elllanero',
    participantCode: '904',
    participantName: 'Banco ficticio D',
    mcc: '5422',
    city: 'Bogotá',
  },
  motoExpress: {
    key: 'motoExpress',
    name: 'Repuestos Moto Express',
    aliasType: 'PHONE',
    aliasValue: '+573156660202',
    participantCode: '902',
    participantName: 'Banco ficticio B',
    mcc: '5013',
    city: 'Soacha',
  },
  harinas: {
    key: 'harinas',
    name: 'Harinas y Levaduras del Valle',
    aliasType: 'ALPHANUMERIC',
    aliasValue: '@harinasdelvalle',
    participantCode: '905',
    participantName: 'Banco ficticio C',
    mcc: '5149',
    city: 'Medellín',
  },
} as const satisfies Record<string, SeedMerchant>;

export type MerchantKey = keyof typeof MERCHANTS;

// ── Planes de cuotas de ejemplo (escritos a mano) ─────────────────────────
type Row = readonly [
  dueDate: string,
  opening: string,
  interest: string,
  principal: string,
  insurance: string,
];

function rows(list: readonly Row[]): ScheduleRow[] {
  return list.map(([dueDate, opening, interest, principal, insurance], index) => {
    const installment = new Decimal(principal).plus(interest).toFixed(2);
    const total = new Decimal(installment).plus(insurance).toFixed(2);
    return {
      number: index + 1,
      dueDate,
      openingBalance: cop(`${opening}.00`),
      interest: cop(`${interest}.00`),
      principal: cop(`${principal}.00`),
      insurance: cop(`${insurance}.00`),
      fee: cop('0.00'),
      installment: cop(installment),
      total: cop(total),
    };
  });
}

/** Planes por compra (las sumas de presentación se toman tal cual: son datos de ejemplo). */
export const SCHEDULES = {
  donPedro180: rows([
    ['2026-12-20', '180000', '5681', '59145', '90'],
    ['2027-01-20', '120855', '3814', '61012', '60'],
    ['2027-02-20', '59843', '1889', '59843', '30'],
  ]),
  mayorista320: rows([
    ['2026-11-20', '320000', '10100', '104350', '160'],
    ['2026-12-20', '215650', '6806', '107648', '108'],
    ['2027-01-20', '108002', '3409', '108002', '54'],
  ]),
  lacteos420: rows([
    ['2026-10-20', '420000', '13256', '70000', '210'],
    ['2026-11-20', '350000', '11046', '70000', '175'],
    ['2026-12-20', '280000', '8837', '70000', '140'],
    ['2027-01-20', '210000', '6628', '70000', '105'],
    ['2027-02-20', '140000', '4419', '70000', '70'],
    ['2027-03-20', '70000', '2209', '70000', '35'],
  ]),
  donPedro45: rows([['2026-11-20', '45000', '1420', '45000', '23']]),
  donPedro52: rows([['2026-11-20', '52000', '1641', '52000', '26']]),
  llanero95: rows([['2026-12-20', '95000', '2998', '95000', '48']]),
  mayorista3500: rows([['2026-12-20', '3500000', '110464', '3500000', '1750']]),
  llanero75: rows([['2026-12-20', '75000', '2367', '75000', '38']]),
  lacteos150: rows([
    ['2026-11-18', '150000', '4734', '74500', '75'],
    ['2026-12-18', '75500', '2383', '75500', '38'],
  ]),
  lacteos88: rows([
    ['2026-12-20', '88000', '2777', '43800', '44'],
    ['2027-01-20', '44200', '1395', '44200', '22'],
  ]),
  hernan600: rows([
    ['2026-08-18', '600000', '18937', '100000', '300'],
    ['2026-09-18', '500000', '15781', '100000', '250'],
    ['2026-10-18', '400000', '12624', '100000', '200'],
    ['2026-11-18', '300000', '9468', '100000', '150'],
    ['2026-12-18', '200000', '6312', '100000', '100'],
    ['2027-01-18', '100000', '3156', '100000', '50'],
  ]),
} as const;

/** Tasas de usura de ejemplo (placeholders: la entidad las actualiza cada periodo). */
export const USURY_RATES = [
  { modality: 'MICROCREDITO', validFrom: '2026-10-01', validTo: '2026-12-31', rateEA: '0.570000' },
  { modality: 'MICROCREDITO', validFrom: '2026-07-01', validTo: '2026-09-30', rateEA: '0.575400' },
  {
    modality: 'CONSUMO_Y_ORDINARIO',
    validFrom: '2026-10-01',
    validTo: '2026-10-31',
    rateEA: '0.248600',
  },
  {
    modality: 'CONSUMO_BAJO_MONTO',
    validFrom: '2026-10-01',
    validTo: '2026-12-31',
    rateEA: '0.639100',
  },
  {
    modality: 'MICROCREDITO_POPULAR_PRODUCTIVO',
    validFrom: '2026-10-01',
    validTo: '2026-12-31',
    rateEA: '0.688200',
  },
] as const;

/** Expedientes recientes para la vista previa de la política de decisión. */
export const RECENT_APPLICATIONS = [
  {
    id: 'app_jairo',
    applicant: 'Jairo Pérez',
    limit: '4000000.00',
    score: 742,
    alerts: [],
    knockOuts: [],
  },
  {
    id: 'app_r01',
    applicant: 'Marta Lucía Ríos',
    limit: '1500000.00',
    score: 711,
    alerts: [],
    knockOuts: [],
  },
  {
    id: 'app_r02',
    applicant: 'Óscar Benavides',
    limit: '1800000.00',
    score: 688,
    alerts: [],
    knockOuts: [],
  },
  {
    id: 'app_r03',
    applicant: 'Yolanda Mosquera',
    limit: '1200000.00',
    score: 702,
    alerts: ['DOCUMENT_NEEDS_VALIDATION'],
    knockOuts: [],
  },
  {
    id: 'app_r04',
    applicant: 'Wilson Arango',
    limit: '900000.00',
    score: 655,
    alerts: [],
    knockOuts: [],
  },
  {
    id: 'app_r05',
    applicant: 'Diana Carolina Peña',
    limit: '2500000.00',
    score: 760,
    alerts: [],
    knockOuts: [],
  },
  {
    id: 'app_r06',
    applicant: 'Fabio Restrepo',
    limit: '700000.00',
    score: 590,
    alerts: [],
    knockOuts: [],
  },
  {
    id: 'app_r07',
    applicant: 'Gloria Inés Cárdenas',
    limit: '1600000.00',
    score: 698,
    alerts: ['SALES_TRIANGULATION_DIVERGENCE'],
    knockOuts: [],
  },
  {
    id: 'app_r08',
    applicant: 'Jhon Fredy Ossa',
    limit: '1000000.00',
    score: 410,
    alerts: [],
    knockOuts: ['BUREAU_CURRENT_DELINQUENCY_GT_30'],
  },
  {
    id: 'app_r09',
    applicant: 'Sandra Milena Ruiz',
    limit: '2000000.00',
    score: 734,
    alerts: [],
    knockOuts: [],
  },
  {
    id: 'app_r10',
    applicant: 'Libardo Quintero',
    limit: '1300000.00',
    score: 676,
    alerts: ['OUT_OF_COVERAGE'],
    knockOuts: [],
  },
  {
    id: 'app_r11',
    applicant: 'Nelly Patiño',
    limit: '800000.00',
    score: 520,
    alerts: [],
    knockOuts: ['SANCTIONS_LIST_MATCH'],
  },
] as const;

/** Usuarios de consola del demo (`02-datos-semilla.md` §5). En el mock, todos los roles. */
export const CONSOLE_USERS = [
  { email: 'admin@demo.paguz.com', fullName: 'Andrea Admin', roles: ['ADMIN'] },
  { email: 'analista@demo.paguz.com', fullName: 'Carlos Analista', roles: ['CREDIT_ANALYST'] },
  { email: 'operador@demo.paguz.com', fullName: 'Paola Operadora', roles: ['PAYMENTS_OPERATOR'] },
  { email: 'conocimiento@demo.paguz.com', fullName: 'Kevin Conocimiento', roles: ['KB_ADMIN'] },
  { email: 'auditor@demo.paguz.com', fullName: 'Lucía Auditora', roles: ['AUDITOR'] },
] as const;

/** Contraseña de los usuarios del mock (la real se entrega por canal privado). */
export const MOCK_PASSWORD = 'demo-paguz-2026';
